<!-- @ts-nocheck -->
<template>

  <div v-if="isComponentEnabled">
    <q-select
      v-model="localvalue"
      filled
      :multiple="true"
      use-chips
      :dense="dense"
      input-debounce="0"
      :options="curOptions"
      :disable="disabled"
      :readonly="disabled"
      :label="getLabel()"
      style="padding-bottom: 0"
      input-style="height: 100px;"
    >
      <template v-slot:selected-item="scope">
        <dtp-chip
          v-if="scope && scope.opt && scope.opt.value === 0"
          dense
          @remove="
            scope.removeAtIndex(scope.index);
            userInteracted();
          "
          :tabindex="scope.tabindex"
        >
          {{ scope.opt.label }}
        </dtp-chip>
        <dtp-chip
          :title="scope.opt.sublabel"
          v-if="scope && scope.opt && scope.opt.value !== 0"
          removable
          dense
          @remove="
            scope.removeAtIndex(scope.index);
            userInteracted();
          "
          :tabindex="scope.tabindex"
        >
          {{ scope.opt.label }}
        </dtp-chip>
      </template>
      <template v-slot:option="scope">
        <dtpItem v-bind="scope.itemProps" @click="userInteracted">
          <dtpItemSection>
            <dtpItemLabel>
              {{ scope.opt.label }}<dtpIcon v-if="scope.opt.hidden"
                                            name="mdi-eye-off"
              /></dtpItemLabel>
            <dtpItemLabel v-if="scope.opt.sublabel" caption>{{ scope.opt.sublabel }}</dtpItemLabel>
          </dtpItemSection>
        </dtpItem>
      </template>
      <template v-slot:append>
        <dtpIcon
          v-if="localvalue && localvalue.length > 0"
          name="cancel"
          @click="
            localvalue = [];
            userInteracted();
          "
          icon-class="cursor-pointer q-field__focusable-action"
        />
      </template>
    </q-select>
  </div>
  <CuiMultiSelect v-else
                  v-model="cuiSelectedItems"
                  :dense="dense"
                  :label="getLabel()"
                  :items="safeOptions"
                  :description="getLabel() || 'Select options'"
                  :wcag-label="getLabel() || 'Multi-select'"
                  :disabled="disabled"
                  @update:modelValue="handleCuiUpdate"
  />
</template>
<script setup lang="ts">
// @ts-nocheck
import { ref, watch, computed } from "vue";
import { QSelect } from "quasar";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { CuiMultiSelect } from "@ist/commonui-components";

export type optiontype = {
  label: string;
  value: any;
  grouptext?: string;
  sublabel?: string;
  hidden?: boolean;
};

defineOptions({ name: "IstChipSelect" });

const { isEnabled } = useFeatureFlags();
const isComponentEnabled = computed(() => isEnabled("IstChipSelect"));

const props = withDefaults(
  defineProps<{
    modelValue?: any;
    disabled?: boolean;
    label?: string;
    options?: Array<optiontype>;
    dense?: boolean;
    inputStyle?: string;
  }>(),
  {
    modelValue: null,
    disabled: false,
    label: undefined,
    options: () => [],
    dense: true,
    inputStyle: undefined
  }
);

const emit = defineEmits<{
  (e: "input", val: any): void;
  (e: "change", val: any): void;
  (e: "update:modelValue", val: any): void;
  (e: "userInteracted"): void;
}>();

const ldense = ref(false);
const curOptions = ref<Array<optiontype>>([]);
const localvalue = ref<any>(null);
// v-model is handled by watchers; no explicit update method needed

const userInteracted = () => {
  emit("userInteracted");
  return true;
};

watch(
  () => props.modelValue,
  (val: any) => {
    localvalue.value = val || undefined;
  },
  { immediate: true }
);

// Emit updates when user changes local selection
watch(
  localvalue,
  (val: any) => {
    emit("input", val);
    emit("change", val);
    emit("update:modelValue", val);
  },
  { deep: true }
);

watch(
  () => props.dense,
  (val: any) => {
    ldense.value = val as boolean;
  },
  { immediate: true }
);

watch(
  () => props.options,
  (val: any) => {
    curOptions.value = val as any;
    let oldtxt = "";
    for (const element of curOptions.value) {
      const s = element.grouptext ? element.grouptext : "";
      if (s !== "" && oldtxt != s) {
        oldtxt = s;
      } else {
        element.grouptext = undefined;
      }
    }
  },
  { immediate: true }
);

const safeOptions = computed(() => {
  if (!curOptions.value) return [];
  return curOptions.value.map((opt: optiontype) => ({
    id: opt.value,
    value: opt.label
  }));
});

// Handle CuiMultiSelect value updates and convert back to original format
const handleCuiUpdate = (newValue: any) => {
  if (!newValue || !Array.isArray(newValue)) {
    localvalue.value = [];
    return;
  }
  // Convert {id, value} back to original optiontype format {value, label}
  localvalue.value = newValue.map((item: any) => {
    // Find the original option to get the full optiontype object
    const originalOption = curOptions.value.find((opt: optiontype) => opt.value === item.id);
    if (originalOption) {
      return originalOption;
    }
    // Fallback: reconstruct optiontype from CuiMultiSelect item
    return { value: item.id, label: item.value };
  });
  userInteracted();
};

// Transform localvalue to CuiMultiSelect format for v-model
const cuiSelectedItems = computed({
  get: () => {
    if (!localvalue.value || !Array.isArray(localvalue.value)) return [];
    // localvalue contains optiontype objects {value, label}, need to match by value
    const selectedValues = localvalue.value.map((item: any) =>
      typeof item === "object" ? item.value : item
    );
    return safeOptions.value.filter(opt =>
      selectedValues.includes(opt.id)
    );
  },
  set: (newValue: any) => {
    handleCuiUpdate(newValue);
  }
});

const getLabel = () => {
  if (!props.label) return;
  const s: string = props.label.charAt(0).toUpperCase() + props.label.substring(1).toLowerCase();
  return s;
};
</script>
<style scoped>
.showerror {
  padding-bottom: 20px !important;
}

:deep(.q-field--dense .q-field__append) {
  padding-top: 22px;
}
</style>
